import React, { useEffect, useRef, useState } from 'react';

const GlobePreloader = ({ onComplete }) => {
  const canvasRef = useRef(null);
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = canvas.width = window.innerWidth;
    let h = canvas.height = window.innerHeight;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);

    // 1. Generate 3D sphere particles (representing Earth dot grid)
    const particles = [];
    const particleCount = 1200;

    for (let i = 0; i < particleCount; i++) {
      // Golden ratio distribution for uniform sphere points
      const phi = Math.acos(-1 + (2 * i) / particleCount);
      const theta = Math.sqrt(particleCount * Math.PI) * phi;

      const x = Math.sin(phi) * Math.cos(theta);
      const y = Math.sin(phi) * Math.sin(theta);
      const z = Math.cos(phi);

      particles.push({
        x, y, z,
        ox: x, oy: y, oz: z, // original coordinates
        color: Math.random() > 0.85 ? '#14b8a6' : '#ffffff' // mix cyan and white dots
      });
    }

    // 2. India coordinates
    const latRad = (20.59 * Math.PI) / 180;
    const lonRad = (78.96 * Math.PI) / 180;

    // Convert India coordinates to Cartesian 3D (Z represents depth, X/Y represent screen grid)
    const targetX = Math.cos(latRad) * Math.cos(lonRad);
    const targetY = Math.cos(latRad) * Math.sin(lonRad);
    const targetZ = Math.sin(latRad);

    const targetVector = { x: targetX, y: targetY, z: targetZ };

    // Initial rotation angles
    let angleX = -0.2;
    let angleY = 0.5;

    // Target angles to center India
    // We rotate around Y to align longitude, and X to align latitude
    const targetAngleY = -lonRad - Math.PI / 2;
    const targetAngleX = -latRad;

    let scale = 160; // initial globe size scale
    let opacity = 1.0;
    let dissolveFactor = 1.0;

    // Radar lock-on variables
    let radarPulse = 0;

    const startTime = Date.now();
    const duration = 6500; // 6.5 seconds total intro

    let frameId;

    const rotateX = (x, y, z, angle) => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return {
        x,
        y: y * cos - z * sin,
        z: y * sin + z * cos
      };
    };

    const rotateY = (x, y, z, angle) => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return {
        x: x * cos + z * sin,
        y,
        z: -x * sin + z * cos
      };
    };

    const draw = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(1.0, elapsed / duration);
      
      // Update loading percentage text
      setPercent(Math.floor(progress * 100));

      ctx.clearRect(0, 0, w, h);
      
      // Draw background space grids / radial glow
      const cx = w / 2;
      const cy = h / 2;
      
      const g = ctx.createRadialGradient(cx, cy, 10, cx, cy, Math.max(w, h) * 0.7);
      g.addColorStop(0, 'rgba(10, 15, 28, 1)');
      g.addColorStop(0.5, 'rgba(6, 6, 8, 1)');
      g.addColorStop(1, '#020203');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      // Animation Timeline logic
      if (progress < 0.45) {
        // Phase 1 (0% to 45%): Free slow rotation
        angleY += 0.006;
        angleX = -0.2 + Math.sin(elapsed * 0.001) * 0.05;
      } else if (progress < 0.82) {
        // Phase 2 (45% to 82%): Lock on India and zoom
        const lockProgress = (progress - 0.45) / (0.82 - 0.45); // 0 to 1
        
        // Easing function (easeInOutCubic)
        const t = lockProgress < 0.5 
          ? 4 * lockProgress * lockProgress * lockProgress 
          : 1 - Math.pow(-2 * lockProgress + 2, 3) / 2;

        // Interpolate rotation angles towards target angles
        angleY = angleY + (targetAngleY - angleY) * t * 0.08;
        angleX = angleX + (targetAngleX - angleX) * t * 0.08;

        // Interpolate zoom scale
        scale = 160 + (980 - 160) * Math.pow(t, 2.5);
      } else {
        // Phase 3 (82% to 100%): Dissolve particles and fade out
        const dissolveProgress = (progress - 0.82) / (1.0 - 0.82); // 0 to 1
        
        // Rapid zoom out/into the camera
        scale = 980 + (2800 - 980) * Math.pow(dissolveProgress, 2);
        
        // Dissolve factor expands coordinates
        dissolveFactor = 1.0 + Math.pow(dissolveProgress, 2.5) * 0.6;
        
        // Fade overall opacity
        opacity = 1.0 - dissolveProgress;
      }

      // Draw particle globe points
      const perspective = 400;

      particles.forEach(p => {
        // Apply dissolve expansion factor
        const x = p.ox * dissolveFactor;
        const y = p.oy * dissolveFactor;
        const z = p.oz * dissolveFactor;

        // Rotate Y (yaw)
        let r1 = rotateY(x, y, z, angleY);
        // Rotate X (pitch)
        let r2 = rotateX(r1.x, r1.y, r1.z, angleX);

        // Perspective projection
        const scaleFactor = perspective / (perspective + r2.z * 1.5);
        const sx = cx + r2.x * scale * scaleFactor;
        const sy = cy + r2.y * scale * scaleFactor;

        // Render point if inside boundaries
        if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
          // Adjust size and opacity based on depth
          const depthAlpha = Math.max(0.1, Math.min(1.0, 0.6 - r2.z));
          const ptSize = Math.max(1.0, (1.8 - r2.z) * scaleFactor * (scale / 160));
          
          ctx.fillStyle = p.color;
          ctx.globalAlpha = depthAlpha * opacity;
          ctx.beginPath();
          ctx.arc(sx, sy, ptSize, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // 4. Render holographic target radar ring on India
      let targetRot = rotateY(targetVector.x, targetVector.y, targetVector.z, angleY);
      let targetRotFinal = rotateX(targetRot.x, targetRot.y, targetRot.z, angleX);

      // Only render target lock indicator if it's facing towards the camera (negative rotated Z)
      if (targetRotFinal.z < 0.1 && progress > 0.45) {
        const scaleFactor = perspective / (perspective + targetRotFinal.z * 1.5);
        const tx = cx + targetRotFinal.x * scale * scaleFactor;
        const ty = cy + targetRotFinal.y * scale * scaleFactor;

        // Lock ring pulse animation
        radarPulse += 0.05;
        const currentPulseRadius = 15 + Math.sin(radarPulse * 2.5) * 5;

        ctx.globalAlpha = opacity;
        
        // Glowing target text details
        ctx.fillStyle = '#14b8a6';
        ctx.font = 'bold 9px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('COORD LOCK: INDIA', tx, ty - currentPulseRadius - 12);
        
        // Holo center dot
        ctx.fillStyle = '#14b8a6';
        ctx.beginPath();
        ctx.arc(tx, ty, 3, 0, Math.PI * 2);
        ctx.fill();

        // Holographic crosshairs
        ctx.strokeStyle = 'rgba(20, 184, 166, 0.45)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(tx, ty, currentPulseRadius, 0, Math.PI * 2);
        ctx.stroke();

        ctx.strokeStyle = 'rgba(20, 184, 166, 0.2)';
        ctx.beginPath();
        ctx.arc(tx, ty, currentPulseRadius * 1.6, 0, Math.PI * 2);
        ctx.stroke();

        // Holographic vertical/horizontal reticle ticks
        ctx.strokeStyle = '#14b8a6';
        ctx.beginPath();
        // vertical
        ctx.moveTo(tx, ty - currentPulseRadius - 5);
        ctx.lineTo(tx, ty - currentPulseRadius + 2);
        ctx.moveTo(tx, ty + currentPulseRadius + 5);
        ctx.lineTo(tx, ty + currentPulseRadius - 2);
        // horizontal
        ctx.moveTo(tx - currentPulseRadius - 5, ty);
        ctx.lineTo(tx - currentPulseRadius + 2, ty);
        ctx.moveTo(tx + currentPulseRadius + 5, ty);
        ctx.lineTo(tx + currentPulseRadius - 2, ty);
        ctx.stroke();
      }

      ctx.globalAlpha = 1.0; // reset alpha

      if (progress < 1.0) {
        frameId = requestAnimationFrame(draw);
      } else {
        onComplete();
      }
    };

    draw();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
    };
  }, [onComplete]);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#020203',
      color: '#ffffff',
      fontFamily: 'Inter, sans-serif',
      overflow: 'hidden',
    }}>
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />

      {/* Futuristic status details in bottom left */}
      <div style={{
        position: 'absolute',
        bottom: '40px',
        left: '40px',
        textAlign: 'left',
        zIndex: 10,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}>
        <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em', color: '#14b8a6' }}>
          SYSTEM PRELOAD SEQUENCE
        </div>
        <div style={{ fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em' }}>
          Establishing Quantum Connection...
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
          Aligning coordinates via orbital link
        </div>
      </div>

      {/* Giant digital loader in center (behind visual elements) */}
      <div style={{
        position: 'absolute',
        zIndex: 5,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          fontSize: '110px',
          fontWeight: 900,
          color: 'rgba(255, 255, 255, 0.015)',
          letterSpacing: '-0.05em',
          userSelect: 'none',
        }}>
          {percent}%
        </div>
      </div>
    </div>
  );
};

export default GlobePreloader;
